import type { Vec3 } from './vec3';
import { safeAcos } from '@/constants/numeric';

/** 3x3 rotation matrix, row-major: [m00,m01,m02,m10,...]. */
export type Mat3 = Float64Array;

export const mat3Identity = (): Mat3 =>
  Float64Array.from([1, 0, 0, 0, 1, 0, 0, 0, 1]);

export function mat3Mul(a: Mat3, b: Mat3, out?: Mat3): Mat3 {
  const o = out ?? new Float64Array(9);
  const a00 = a[0], a01 = a[1], a02 = a[2];
  const a10 = a[3], a11 = a[4], a12 = a[5];
  const a20 = a[6], a21 = a[7], a22 = a[8];
  const b00 = b[0], b01 = b[1], b02 = b[2];
  const b10 = b[3], b11 = b[4], b12 = b[5];
  const b20 = b[6], b21 = b[7], b22 = b[8];
  o[0] = a00 * b00 + a01 * b10 + a02 * b20;
  o[1] = a00 * b01 + a01 * b11 + a02 * b21;
  o[2] = a00 * b02 + a01 * b12 + a02 * b22;
  o[3] = a10 * b00 + a11 * b10 + a12 * b20;
  o[4] = a10 * b01 + a11 * b11 + a12 * b21;
  o[5] = a10 * b02 + a11 * b12 + a12 * b22;
  o[6] = a20 * b00 + a21 * b10 + a22 * b20;
  o[7] = a20 * b01 + a21 * b11 + a22 * b21;
  o[8] = a20 * b02 + a21 * b12 + a22 * b22;
  return o;
}

export function mat3Transpose(a: Mat3): Mat3 {
  return Float64Array.from([a[0], a[3], a[6], a[1], a[4], a[7], a[2], a[5], a[8]]);
}

export function mat3Apply(m: Mat3, v: Vec3): Vec3 {
  return {
    x: m[0] * v.x + m[1] * v.y + m[2] * v.z,
    y: m[3] * v.x + m[4] * v.y + m[5] * v.z,
    z: m[6] * v.x + m[7] * v.y + m[8] * v.z,
  };
}

/** Rodrigues' rotation formula. `axis` must be a unit vector. */
export function rotationAboutAxis(axis: Vec3, angle: number, out?: Mat3): Mat3 {
  const m = out ?? new Float64Array(9);
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const t = 1 - c;
  const { x, y, z } = axis;
  m[0] = t * x * x + c;
  m[1] = t * x * y - s * z;
  m[2] = t * x * z + s * y;
  m[3] = t * x * y + s * z;
  m[4] = t * y * y + c;
  m[5] = t * y * z - s * x;
  m[6] = t * x * z - s * y;
  m[7] = t * y * z + s * x;
  m[8] = t * z * z + c;
  return m;
}

/** Skew-symmetric cross-product matrix [v]x. */
export function crossMatrix(v: Vec3): Mat3 {
  return Float64Array.from([0, -v.z, v.y, v.z, 0, -v.x, -v.y, v.x, 0]);
}

/** Inverse of crossMatrix: extract the axial vector of a skew matrix. */
export function veeSkew(m: Mat3): Vec3 {
  return { x: (m[7] - m[5]) / 2, y: (m[2] - m[6]) / 2, z: (m[3] - m[1]) / 2 };
}

/**
 * Logarithm map SO(3) -> so(3), returned as a rotation vector (axis * angle).
 * Zero exactly when the matrix is the identity, which makes it a clean residual
 * for the loop-closure constraint. Handles the angle ~ pi branch explicitly.
 */
export function rotationLog(m: Mat3): Vec3 {
  const trace = m[0] + m[4] + m[8];
  const cosTheta = (trace - 1) / 2;
  const theta = safeAcos(cosTheta);
  if (theta < 1e-8) {
    // Near identity: log(R) ~ skew part, second order accurate.
    return veeSkew(m);
  }
  if (Math.PI - theta < 1e-6) {
    // Near pi: recover the axis from the symmetric part B = (R + I)/2 = a a^T.
    const b = [
      (m[0] + 1) / 2,
      (m[4] + 1) / 2,
      (m[8] + 1) / 2,
    ];
    let i = 0;
    if (b[1] > b[i]) i = 1;
    if (b[2] > b[i]) i = 2;
    const d = Math.sqrt(Math.max(b[i], 0));
    let ax: number, ay: number, az: number;
    if (i === 0) {
      ax = d;
      ay = d === 0 ? 0 : (m[1] + m[3]) / 4 / d;
      az = d === 0 ? 0 : (m[2] + m[6]) / 4 / d;
    } else if (i === 1) {
      ay = d;
      ax = d === 0 ? 0 : (m[1] + m[3]) / 4 / d;
      az = d === 0 ? 0 : (m[5] + m[7]) / 4 / d;
    } else {
      az = d;
      ax = d === 0 ? 0 : (m[2] + m[6]) / 4 / d;
      ay = d === 0 ? 0 : (m[5] + m[7]) / 4 / d;
    }
    const n = Math.hypot(ax, ay, az) || 1;
    return { x: (ax / n) * theta, y: (ay / n) * theta, z: (az / n) * theta };
  }
  const k = theta / (2 * Math.sin(theta));
  return {
    x: (m[7] - m[5]) * k,
    y: (m[2] - m[6]) * k,
    z: (m[3] - m[1]) * k,
  };
}
